import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion, useInView, AnimatePresence } from "framer-motion";
import logoDark from "./assets/logo-dark.png";
import logoLight from "./assets/logo-light.png";
import ynpPose from "./assets/ynp-pose.png";
import whatsappIcon from "./assets/whatsapp.jpg";
import { 
  MapPin, Coffee, DollarSign, Building2, Hospital, GraduationCap, 
  Warehouse, Stethoscope, Building, Home, CheckCircle, Phone, 
  Mail, MessageCircle, Send, X, ChevronDown, Star, Menu, ArrowRight,
  Users, Clock, Zap, Shield, Calendar, ChevronRight, Download,
  Filter, Eye, Check, XCircle, LogOut, BarChart3, Calculator,
  BookOpen, Share2, Twitter, Linkedin, Facebook, ArrowLeft, Flame, TrendingUp
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "./components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import { Badge } from "./components/ui/badge";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://ynp.net";
const API = `${BACKEND_URL}/api`;
const GTM_ID = process.env.REACT_APP_GTM_ID;
const FB_PIXEL_ID = process.env.REACT_APP_FB_PIXEL_ID;
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || "https://calendly.com/ypdailyroast/10min";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Section wrapper with animation
const AnimatedSection = ({ children, className = "", id, ...props }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      id={id}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
      {...props}
    >
      {children}
    </motion.section>
  );
};

// ==================== TRACKING ====================

// Initialize GTM
const initGTM = () => {
  if (typeof window !== 'undefined' && GTM_ID && GTM_ID !== 'GTM-XXXXXXX') {
    const script = document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${GTM_ID}');
    `;
    document.head.appendChild(script);
  }
};

// Initialize Meta Pixel
const initFBPixel = () => {
  if (typeof window !== 'undefined' && FB_PIXEL_ID && FB_PIXEL_ID !== 'FB-PIXEL-XXXX') {
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${FB_PIXEL_ID}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
  }
};

// Track conversion
const trackConversion = () => {
  if (typeof window !== 'undefined') {
    // GTM conversion event
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'form_submission',
        'form_name': 'contact_form'
      });
    }
    // FB Pixel conversion
    if (window.fbq) {
      window.fbq('track', 'Lead');
    }
  }
};

// Track custom events
const trackEvent = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined') {
    // GTM event
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': eventName,
        ...eventData
      });
    }
    // Console log for debugging
    console.log('Analytics Event:', eventName, eventData);
  }
};

// ==================== NAVIGATION ====================

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: isHomePage ? "#how-it-works" : "/#how-it-works", label: "How It Works" },
    { href: isHomePage ? "#benefits" : "/#benefits", label: "Benefits" },
    { href: "/blog", label: "Blog", isRoute: true },
    { href: isHomePage ? "#contact" : "/#contact", label: "Contact" }
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-primary'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo">
            {/* <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center"> */}
              <img src={isScrolled ? logoDark : logoLight} alt="Y&P's Daily Roast" className="h-14 w-auto object-contain" />
              {/* </div> */}
              <span className={`font-bold text-xl ${isScrolled ? 'text-foreground' : 'text-white'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                Y&P's Daily Roast
              </span>
            
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`nav-link font-medium transition-colors ${
                    isScrolled ? 'text-foreground/80 hover:text-foreground' : 'text-white/80 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className={`nav-link font-medium transition-colors ${
                    isScrolled ? 'text-foreground/80 hover:text-foreground' : 'text-white/80 hover:text-white'
                  }`}
                >
                  {link.label}
                </a>
              )
            ))}
            <a href="tel:+1 (226) 988-7379">
              <Button 
                className={`rounded-full px-6 ${
                  isScrolled 
                    ? 'bg-primary text-white hover:bg-primary/90' 
                    : 'bg-white text-primary hover:bg-white/90'
                }`}
                data-testid="nav-call-btn"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
            </a>
          </div>

          <button 
            className={`md:hidden p-2 ${isScrolled ? 'text-foreground' : 'text-white'}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white/95 backdrop-blur-md border-t border-border"
          >
            <div className="px-6 py-4 space-y-4">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="block py-2 text-foreground font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    className="block py-2 text-foreground font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                )
              ))}
              <a href="tel:+1 (226) 988-7379" className="block">
                <Button className="w-full rounded-full bg-primary text-white">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now
                </Button>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// ==================== HERO SECTION ====================

const HeroSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCheckLocation = () => {
    trackEvent('check_location_click');
    setIsModalOpen(true);
  };

  return (
    <>
      <section className="relative min-h-screen hero-gradient coffee-pattern overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.span 
                className="inline-block px-4 py-2 bg-secondary/50 text-primary rounded-full text-sm font-semibold mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                Ontario's Trusted Coffee Partner
              </motion.span>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
                Turn Your Empty Space Into{' '}
                <span className="text-accent">Passive Income</span>
              </h1>
              
              <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl">
                Install a premium coffee vending machine in your location and earn commission on every cup sold. Zero investment, zero hassle.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  onClick={handleCheckLocation}
                  className="rounded-full bg-primary text-white hover:bg-primary/90 px-8 py-6 text-lg btn-hover shadow-lg"
                  data-testid="hero-check-btn"
                >
                  Check My Location
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <a href="tel:+1 (226) 988-7379">
                  <Button 
                    variant="outline"
                    className="rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-6 text-lg btn-hover"
                    data-testid="hero-call-btn"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now
                  </Button>
                </a>
              </div>

              <div className="flex items-center gap-6 flex-wrap">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-accent" />
                  <span className="text-sm text-muted-foreground">Zero Investment</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-accent" />
                  <span className="text-sm text-muted-foreground">Full Maintenance</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-accent" />
                  <span className="text-sm text-muted-foreground">Monthly Payouts</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative">
                <div className="absolute -top-10 -right-10 w-72 h-72 bg-accent/20 rounded-full blur-3xl" />
                <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-secondary/50 rounded-full blur-3xl" />
                
                {/* Coffee Vending Machine Image */}
                <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1653491948869-cea123870879?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxzZWxmJTIwc2VydmljZSUyMGNvZmZlZSUyMGtpb3NrJTIwbWFjaGluZSUyMHRvdWNoc2NyZWVufGVufDB8fHx8MTc3MjU5Mzk5MHww&ixlib=rb-4.1.0&q=85" 
                    alt="Coffee Vending Machine in Office" 
                    className="w-full h-auto object-cover"
                  />
                </div>

                {/* Floating Commission Card */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl z-20"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-accent/20 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-7 h-7 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Average Monthly</p>
                      <p className="text-2xl font-bold text-foreground">$600+</p>
                      <p className="text-xs text-accent">Commission</p>
                    </div>
                  </div>
                </motion.div>

                {/* Floating Cups Card */}
                <motion.div
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
                  className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl z-20"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Coffee className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Daily Sales</p>
                      <p className="text-lg font-bold text-foreground">80+ cups</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Hero Qualification Modal */}
      <QualificationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

// ==================== REVENUE CALCULATOR SECTION ====================

// ==================== SOCIAL PROOF COUNTER ====================

const SocialProofCounter = () => {
  const initialNumber = Math.floor(Math.random() * 61) + 40; // Random 40–100 shown immediately
  const [stats, setStats] = useState({ display_count: initialNumber, weekly_leads: 0 });
  const [isLoading, setIsLoading] = useState(false); // Never show loading state
  const [displayNumber, setDisplayNumber] = useState(initialNumber);
  const [randomizedNumber, setRandomizedNumber] = useState(initialNumber);
 
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats/weekly-leads`);
        setStats(response.data);
        
        // If count is less than 30, generate a random number between 40-100 for display
        const actualCount = response.data.display_count || 0;
        if (actualCount < 30) {
          const randomNum = Math.floor(Math.random() * 61) + 40; // Random between 40-100
          setRandomizedNumber(randomNum); 
          setDisplayNumber(randomNum);
        } else {
          setRandomizedNumber(null);
          setDisplayNumber(actualCount);
        }
      } catch (error) {
        console.error('Failed to fetch weekly stats:', error);
        // Set default fallback values with randomization
        const randomNum = Math.floor(Math.random() * 61) + 40;
        setStats({ display_count: 15, weekly_leads: 0 });
        setRandomizedNumber(randomNum);
        setDisplayNumber(randomNum);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, []);

  return (
    <motion.div 
      className="pt-[6rem] pb-4 flex justify-center"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      data-testid="social-proof-counter"
    >
      <div className="mx-6 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-full shadow-lg">
        <div className="flex items-center justify-center gap-3 text-white text-base md:text-md whitespace-nowrap">
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <Flame className="w-5 h-5" />
          </motion.div>
          {isLoading ? (
            <span className="font-semibold">Loading...</span>
          ) : (
            <motion.span 
              className="font-semibold text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <motion.span
                key={displayNumber}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="font-bold text-md"
              >
                {displayNumber}
              </motion.span>
              {randomizedNumber !== null ? (
                <>+ </>
              ) : (
                <> </>
              )}
              locations inquired this week
              <span className="hidden sm:inline"> — Join them today!</span>
            </motion.span>
          )}
          <motion.div
            animate={{ x: [0, 5, 0] }}
            transition={{ duration: 1, repeat: Infinity }}
          >
            <TrendingUp className="w-5 h-5" />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

const RevenueCalculator = () => {
  const [cupsPerDay, setCupsPerDay] = useState(80);
  const [pricePerCup, setPricePerCup] = useState(2.50);

  const dailyRevenue = cupsPerDay * pricePerCup;
  const monthlyRevenue = dailyRevenue * 30;
  const commission = monthlyRevenue * 0.10;

  return (
    <AnimatedSection className="py-20 bg-white" id="calculator">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-testid="revenue-calculator">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-secondary/50 text-primary rounded-full text-sm font-semibold mb-4">
            Earnings Estimator
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
            Estimate Your Monthly Commission
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Use this quick calculator to estimate how much your location could earn.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <Card className="border-0 shadow-xl bg-gradient-to-br from-muted/30 to-white overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 mb-10">
                {/* Cups Per Day Input */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-3">
                    Estimated cups sold per day
                  </label>
                  <div className="relative">
                    <Input
                      type="number"
                      value={cupsPerDay}
                      onChange={(e) => setCupsPerDay(Math.max(0, parseInt(e.target.value) || 0))}
                      className="h-14 text-xl font-bold text-center bg-white border-2 border-border rounded-xl focus:border-accent"
                      data-testid="calc-cups-input"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">cups</span>
                  </div>
                  <input
                    type="range"
                    min="20"
                    max="300"
                    value={cupsPerDay}
                    onChange={(e) => setCupsPerDay(parseInt(e.target.value))}
                    className="w-full mt-3 accent-accent"
                  />
                </div>

                {/* Price Per Cup Input */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-3">
                    Price per coffee
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      type="number"
                      step="0.25"
                      value={pricePerCup}
                      onChange={(e) => setPricePerCup(Math.max(0, parseFloat(e.target.value) || 0))}
                      className="h-14 text-xl font-bold text-center bg-white border-2 border-border rounded-xl focus:border-accent pl-8"
                      data-testid="calc-price-input"
                    />
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="0.25"
                    value={pricePerCup}
                    onChange={(e) => setPricePerCup(parseFloat(e.target.value))}
                    className="w-full mt-3 accent-accent"
                  />
                </div>
              </div>

              {/* Calculation Display */}
              <div className="bg-primary/5 rounded-2xl p-6 mb-8">
                <div className="text-center space-y-2 mb-6">
                  <p className="text-muted-foreground">
                    <span className="font-bold text-foreground">{cupsPerDay}</span> cups × 
                    <span className="font-bold text-foreground"> ${pricePerCup.toFixed(2)}</span> = 
                    <span className="font-bold text-accent"> ${dailyRevenue.toFixed(2)}</span> per day
                  </p>
                  <p className="text-muted-foreground">
                    <span className="font-bold text-foreground">${dailyRevenue.toFixed(2)}</span> × 
                    <span className="font-bold text-foreground"> 30 days</span> = 
                    <span className="font-bold text-accent"> ${monthlyRevenue.toLocaleString()}</span> monthly revenue
                  </p>
                </div>

                <div className="border-t-2 border-dashed border-accent/30 pt-6">
                  <p className="text-center text-sm text-muted-foreground mb-2">Estimated 10% commission:</p>
                  <motion.div
                    key={commission}
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-center"
                  >
                    <span className="text-5xl md:text-6xl font-bold text-accent" style={{ fontFamily: 'Playfair Display, serif' }}>
                      ${commission.toLocaleString()}
                    </span>
                    <span className="text-xl text-muted-foreground">/month</span>
                  </motion.div>
                  <p className="text-center text-accent font-semibold mt-2">passive income</p>
                </div>
              </div>

              <p className="text-center text-sm text-muted-foreground italic">
                * Actual earnings vary depending on location traffic and sales volume.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </AnimatedSection>
  );
};

// ==================== TRUSTED LOCATIONS SECTION ====================

// const TrustedLocationsSection = () => {
//   const locations = [
//     { icon: Building2, label: "Offices", description: "Break rooms & lobbies" },
//     { icon: Hospital, label: "Hospitals", description: "Waiting areas & staff rooms" },
//     { icon: GraduationCap, label: "Schools & Colleges", description: "Cafeterias & common areas" },
//     { icon: Building, label: "Commercial Buildings", description: "Lobbies & common spaces" },
//     { icon: Warehouse, label: "Warehouses", description: "Employee break areas" },
//     { icon: Home, label: "Apartment Complexes", description: "Amenity rooms & lobbies" }
//   ];

//   return (
//     <AnimatedSection className="py-16 bg-muted/30">
//       <div className="max-w-7xl mx-auto px-6 md:px-12" data-testid="trusted-locations">
//         <div className="text-center mb-12">
//           <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
//             Perfect Locations For Our Coffee Machines
//           </h2>
//         </div>

//         <motion.div
//           variants={staggerContainer}
//           initial="hidden"
//           whileInView="visible"
//           viewport={{ once: true }}
//           className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
//         >
//           {locations.map((loc, index) => (
//             <motion.div
//               key={index}
//               variants={fadeInUp}
//               whileHover={{ y: -5, scale: 1.02 }}
//               className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-lg transition-all cursor-pointer"
//             >
//               <div className="w-14 h-14 mx-auto bg-primary/10 rounded-xl flex items-center justify-center mb-4">
//                 <loc.icon className="w-7 h-7 text-primary" />
//               </div>
//               <h3 className="font-semibold text-foreground mb-1">{loc.label}</h3>
//               <p className="text-xs text-muted-foreground">{loc.description}</p>
//             </motion.div>
//           ))}
//         </motion.div>
//       </div>
//     </AnimatedSection>
//   );
// };

// ==================== TRUST BENEFITS BAR ====================

const TrustBenefitsBar = () => {
  const benefits = [
    { icon: CheckCircle, text: "Zero installation cost" },
    { icon: CheckCircle, text: "Fully maintained machines" },
    { icon: CheckCircle, text: "Passive income opportunity" },
    { icon: CheckCircle, text: "Ontario-based service" }
  ];

  return (
    <div className="py-8 bg-primary" data-testid="trust-benefits-bar">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-wrap justify-center gap-6 md:gap-12">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className="flex items-center gap-2 text-white"
            >
              <benefit.icon className="w-5 h-5 text-accent" />
              <span className="font-medium">{benefit.text}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== EXAMPLE EARNINGS SECTION ====================

const ExampleEarningsSection = () => {
  return (
    <AnimatedSection className="py-20 bg-gradient-to-br from-accent/10 to-secondary/20">
      <div className="max-w-4xl mx-auto px-6 md:px-12" data-testid="example-earnings">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-2 bg-white text-primary rounded-full text-sm font-semibold mb-4">
            Real Example
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">
            Example Location Earnings
          </h2>
        </div>

        <Card className="border-0 shadow-xl overflow-hidden">
          <CardContent className="p-8 md:p-12">
            <div className="space-y-6">
              {/* Step 1 */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex items-center gap-4 p-4 bg-muted/30 rounded-xl"
              >
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white font-bold">1</div>
                <div className="flex-1">
                  <p className="text-foreground">
                    <span className="font-bold">80 cups/day</span> × <span className="font-bold">$2.50</span>
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-xl font-bold text-accent">$200/day</span>
                </div>
              </motion.div>

              {/* Step 2 */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                viewport={{ once: true }}
                className="flex items-center gap-4 p-4 bg-muted/30 rounded-xl"
              >
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white font-bold">2</div>
                <div className="flex-1">
                  <p className="text-foreground">
                    <span className="font-bold">$200</span> × <span className="font-bold">30 days</span>
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-xl font-bold text-accent">$6,000/month</span>
                </div>
              </motion.div>

              {/* Result */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
                className="bg-primary rounded-2xl p-6 text-center text-white mt-6"
              >
                <p className="text-sm text-white/80 mb-2">Even at just 10% commission</p>
                <p className="text-4xl md:text-5xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                  $600/month
                </p>
                <p className="text-lg text-accent mt-2">passive income</p>
              </motion.div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AnimatedSection>
  );
};

// ==================== QUALIFICATION SECTION ====================

// ==================== QUALIFICATION MODAL ====================

const QualificationModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [locationType, setLocationType] = useState('');
  const [formData, setFormData] = useState({
    city: '',
    dailyVisitors: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const locationTypes = [
    { id: 'office', label: 'Office', icon: Building2 },
    { id: 'hospital', label: 'Hospital', icon: Hospital },
    { id: 'school', label: 'School / College', icon: GraduationCap },
    { id: 'warehouse', label: 'Warehouse', icon: Warehouse },
    { id: 'commercial', label: 'Commercial Building', icon: Building },
    { id: 'apartment', label: 'Apartment / Condo', icon: Home },
    { id: 'other', label: 'Other', icon: MapPin }
  ];

  const handleLocationSelect = (type) => {
    setLocationType(type);
    trackEvent('qualification_step1_selected', { location_type: type });
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name: '', // Will be collected later or via phone
        email: '', // Optional for quick qualification
        phone: formData.phone,
        company: '',
        city: formData.city,
        location_type: locationType,
        foot_traffic: formData.dailyVisitors,
        message: `Quick qualification form - ${formData.dailyVisitors} daily visitors`
      });

      trackEvent('qualification_form_submit', {
        location_type: locationType,
        city: formData.city,
        daily_visitors: formData.dailyVisitors
      });
      trackConversion();
      setIsSuccess(true);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setLocationType('');
    setFormData({ city: '', dailyVisitors: '', phone: '' });
    setIsSuccess(false);
    onClose();
  };

  const handleBack = () => {
    setStep(1);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        onClick={handleClose}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          data-testid="qualification-modal"
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted transition-colors z-10"
            data-testid="qualification-modal-close"
          >
            <X className="w-5 h-5 text-foreground" />
          </button>

          {/* Success State */}
          {isSuccess ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-8 text-center"
            >
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-4">Thank You!</h2>
              <p className="text-muted-foreground mb-8">
                Our team will contact you within 24 hours to discuss installing a coffee machine at your location.
              </p>
              <Button
                onClick={handleClose}
                className="rounded-full bg-primary text-white hover:bg-primary/90 px-8 py-6"
                data-testid="qualification-success-close"
              >
                Close
              </Button>
            </motion.div>
          ) : step === 1 ? (
            /* Step 1: Location Type Selection */
            <div className="p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                  What Type of Location Do You Have?
                </h2>
                <p className="text-muted-foreground">
                  Locations with 100+ daily visitors can earn{' '}
                  <span className="text-accent font-semibold">$400–$900/month</span> in commission.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {locationTypes.map((type) => (
                  <motion.button
                    key={type.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleLocationSelect(type.id)}
                    className="flex flex-col items-center gap-3 p-5 rounded-2xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all"
                    data-testid={`location-type-${type.id}`}
                  >
                    <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
                      <type.icon className="w-7 h-7 text-primary" />
                    </div>
                    <span className="font-semibold text-foreground text-sm">{type.label}</span>
                  </motion.button>
                ))}
              </div>
            </div>
          ) : (
            /* Step 2: Location Details Form */
            <div className="p-8">
              {/* Back Button */}
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
                data-testid="qualification-back-btn"
              >
                <ChevronDown className="w-4 h-4 rotate-90" />
                <span className="text-sm">Back</span>
              </button>

              <div className="mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                  Tell Us About Your Location
                </h2>
                {/* Selected Location Type Badge */}
                <div className="flex items-center gap-2 mt-3">
                  <span className="text-sm text-muted-foreground">Location Type:</span>
                  <Badge variant="secondary" className="bg-primary/10 text-primary capitalize">
                    {locationTypes.find(t => t.id === locationType)?.label || locationType}
                  </Badge>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* City Input */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    City
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter your city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                    className="h-14 rounded-xl bg-muted/30 border-border"
                    data-testid="qualification-city-input"
                  />
                </div>

                {/* Daily Visitors Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Estimated Daily Visitors
                  </label>
                  <Select
                    value={formData.dailyVisitors}
                    onValueChange={(value) => setFormData({ ...formData, dailyVisitors: value })}
                    required
                  >
                    <SelectTrigger 
                      className="h-14 rounded-xl bg-muted/30 border-border"
                      data-testid="qualification-visitors-select"
                    >
                      <SelectValue placeholder="Select daily visitors" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="40-100">40–100 visitors</SelectItem>
                      <SelectItem value="100-300">100–300 visitors</SelectItem>
                      <SelectItem value="300-500">300–500 visitors</SelectItem>
                      <SelectItem value="500+">500+ visitors</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Phone Number Input */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Phone Number <span className="text-destructive">*</span>
                  </label>
                  <Input
                    type="tel"
                    placeholder="Enter your phone number"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="h-14 rounded-xl bg-muted/30 border-border"
                    data-testid="qualification-phone-input"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting || !formData.phone || !formData.dailyVisitors}
                  className="w-full h-14 rounded-full bg-primary text-white hover:bg-primary/90 text-lg font-semibold shadow-lg"
                  data-testid="qualification-submit-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                      />
                      Submitting...
                    </span>
                  ) : (
                    <>
                      Submit Location
                      <Send className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  We'll contact you within 24 hours to discuss your location.
                </p>
              </form>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ==================== QUALIFICATION SECTION ====================

const QualificationSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const qualifications = [
    { icon: Users, text: "40+ daily visitors" },
    { icon: Building2, text: "Indoor accessible space" },
    { icon: Zap, text: "Standard power outlet" },
    { icon: Shield, text: "Public or employee access area" }
  ];

  const handleOpenModal = () => {
    trackEvent('check_location_click');
    setIsModalOpen(true);
  };

  return (
    <>
      <AnimatedSection className="py-16 bg-accent/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-testid="qualification-section">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <span className="inline-block px-4 py-2 bg-secondary/50 text-primary rounded-full text-sm font-semibold mb-4">
                  Quick Check
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Does Your Location Qualify?
                </h2>
                <p className="text-muted-foreground mb-6">
                  Most commercial spaces with regular foot traffic are perfect candidates for our coffee machines.
                </p>
              </div>
              <div className="space-y-4">
                {qualifications.map((qual, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-4 p-4 bg-muted/30 rounded-xl"
                  >
                    <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center">
                      <qual.icon className="w-6 h-6 text-accent" />
                    </div>
                    <span className="font-medium text-foreground">{qual.text}</span>
                    <CheckCircle className="w-5 h-5 text-green-500 ml-auto" />
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="mt-8 text-center">
              <Button 
                onClick={handleOpenModal}
                className="rounded-full bg-primary text-white hover:bg-primary/90 px-8 py-6 text-lg btn-hover shadow-lg"
                data-testid="qualification-cta-btn"
              >
                Check My Location
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Qualification Modal */}
      <QualificationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

// ==================== HOW IT WORKS ====================

const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      icon: MapPin,
      title: "You Provide Space",
      description: "Just 1 sq meter of floor space with a power outlet. That's all we need to get started."
    },
    {
      number: "02",
      icon: Coffee,
      title: "We Install & Manage",
      description: "We handle installation, restocking, cleaning, and all maintenance. Complete hands-off experience."
    },
    {
      number: "03",
      icon: DollarSign,
      title: "You Earn Commission",
      description: "Receive monthly payouts directly to your account. More cups sold = more you earn."
    }
  ];

  return (
    <AnimatedSection className="py-24 md:py-32 bg-white" id="how-it-works">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-testid="how-it-works-section">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-secondary/50 text-primary rounded-full text-sm font-semibold mb-4">
            Simple Process
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
            How It Works
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Three simple steps to start earning passive income from your unused space.
          </p>
        </div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8"
        >
          {steps.map((step, index) => (
            <motion.div key={index} variants={fadeInUp}>
              <Card className="h-full bg-muted/30 border-0 shadow-none hover:bg-muted/50 transition-all card-hover">
                <CardContent className="p-8">
                  <span className="step-number">{step.number}</span>
                  <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-6 -mt-6">
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">{step.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// ==================== LOCATIONS SECTION ====================

const LocationsSection = () => {
  const locations = [
    {
      title: "Corporate Offices",
      image: "https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjBsb2JieSUyMGludGVyaW9yfGVufDB8fHx8MTc3MjQ4MTYxN3ww&ixlib=rb-4.1.0&q=85",
      icon: Building2,
      span: "md:col-span-2 md:row-span-2",
      link: "/office-coffee-machines"
    },
    {
      title: "Hospitals & Clinics",
      image: "https://images.unsplash.com/photo-1581982231900-6a1a46b744c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxob3NwaXRhbCUyMHdhaXRpbmclMjByb29tJTIwbW9kZXJufGVufDB8fHx8MTc3MjQ4MTYxOHww&ixlib=rb-4.1.0&q=85",
      icon: Hospital,
      span: "md:col-span-1",
      link: "/hospital-coffee-vending"
    },
    {
      title: "Warehouses",
      image: "https://images.unsplash.com/photo-1765366417046-f46361a7f26f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBlbXBsb3llZSUyMGJyZWFrJTIwcm9vbXxlbnwwfHx8fDE3NzI0ODE2MjV8MA&ixlib=rb-4.1.0&q=85",
      icon: Warehouse,
      span: "md:col-span-1"
    },
    {
      title: "Schools & Universities",
      image: "https://images.unsplash.com/photo-1460648291905-271dfe6082a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHx1bml2ZXJzaXR5JTIwaGFsbHdheSUyMG1vZGVybnxlbnwwfHx8fDE3NzI0ODE2MTl8MA&ixlib=rb-4.1.0&q=85",
      icon: GraduationCap,
      span: "md:col-span-1",
      link: "/school-coffee-solutions"
    },
    {
      title: "Commercial Buildings",
      image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800",
      icon: Building,
      span: "md:col-span-1"
    },
    {
      title: "Apartment Complexes",
      image: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800",
      icon: Home,
      span: "md:col-span-1",
      link: "/property-management-partnerships"
    }
  ];

  return (
    <AnimatedSection className="py-24 md:py-32 bg-muted/30 coffee-pattern" id="locations">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-testid="locations-section">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-white text-primary rounded-full text-sm font-semibold mb-4">
            Our Locations
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
            Where We Install
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            From corporate offices to healthcare facilities, we serve diverse locations across Ontario.
          </p>
        </div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-4 gap-4 auto-rows-[200px]"
        >
          {locations.map((location, index) => (
            <motion.div 
              key={index} 
              variants={fadeInUp}
              className={`location-card group cursor-pointer ${location.span}`}
            >
              {location.link ? (
                <Link to={location.link} className="absolute inset-0 z-20" />
              ) : null}
              <img 
                src={location.image} 
                alt={location.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-lg flex items-center justify-center">
                    <location.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-white font-bold text-lg">{location.title}</h3>
                  {location.link && <ChevronRight className="w-5 h-5 text-white ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// ==================== BENEFITS SECTION ====================

const BenefitsSection = () => {
  const benefits = [
    { title: "Zero Investment Required", description: "We provide the machine, you provide the space. No upfront costs." },
    { title: "Fully Managed & Maintained", description: "From installation to restocking, we handle everything." },
    { title: "Hygienic & High-Quality", description: "Premium machines with regular cleaning and quality control." },
    { title: "Passive Income Stream", description: "Earn commission on every cup without any effort." },
    { title: "Fast Installation", description: "Get set up within days, not weeks." },
    { title: "Local Ontario Service", description: "Dedicated support from our Ontario-based team." }
  ];

  return (
    <AnimatedSection className="py-24 md:py-32 bg-white" id="benefits">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-testid="benefits-section">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="inline-block px-4 py-2 bg-secondary/50 text-primary rounded-full text-sm font-semibold mb-4">
              Why Choose Us
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              Why Partner With Us?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join dozens of property owners across Ontario who are already earning passive income with our coffee vending solutions.
            </p>
            <div className="gold-line mb-8" />
            
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid sm:grid-cols-2 gap-4"
            >
              {benefits.map((benefit, index) => (
                <motion.div 
                  key={index} 
                  variants={fadeInUp}
                  className="flex items-start gap-3 p-4 rounded-xl hover:bg-muted/30 transition-all"
                >
                  <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">{benefit.title}</h4>
                    <p className="text-sm text-muted-foreground">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          <div className="relative">
            <div className="absolute -top-10 -right-10 w-64 h-64 bg-accent/10 rounded-full blur-3xl" />
            <img 
              src="https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=800" 
              alt="Coffee Machine"
              className="relative z-10 rounded-3xl shadow-2xl w-full"
            />
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// ==================== FOUNDER SECTION ====================

const FounderSection = () => {
  return (
    <AnimatedSection className="py-24 md:py-32 bg-primary" id="about">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-testid="founder-section">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative w-72 h-72 md:w-96 md:h-96 mx-auto">
              <div className="absolute inset-0 bg-accent/30 rounded-full blur-3xl" />
              <img 
                src={ynpPose} 
                alt="Chandra Yarlagadda - Founder"
                className="relative z-10 w-full h-full object-cover rounded-full founder-image"
              />
            </div>
          </motion.div>

          <div className="text-white">
            <span className="inline-block px-4 py-2 bg-white/10 text-white rounded-full text-sm font-semibold mb-4">
              Meet the Founder
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Chandra Yarlagadda
            </h2>
            <div className="gold-line mb-8" style={{ background: 'linear-gradient(90deg, #D4AF37, #F3E5AB)' }} />
            <p className="text-lg text-white/80 leading-relaxed mb-6">
              Chandra Yarlagadda is dedicated to providing convenient, high-quality coffee solutions across Ontario. 
              His mission is to create win-win partnerships that benefit property owners while delivering great 
              coffee experiences to their visitors and employees.
            </p>
            <p className="text-lg text-white/80 leading-relaxed mb-8">
              With a passion for quality and customer service, Chandra has built Y&P's Daily Roast into a trusted 
              name in Ontario's vending industry.
            </p>
            <blockquote className="border-l-4 border-accent pl-6 italic text-white/90" style={{ fontFamily: 'Playfair Display, serif' }}>
              "Every break should be an experience. We're not just vending coffee — we're creating moments that 
              energize and inspire."
            </blockquote>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// ==================== COMMISSION SECTION ====================

const CommissionSection = () => {
  return (
    <AnimatedSection className="py-24 md:py-32 bg-gradient-to-br from-secondary/30 to-muted/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-testid="commission-section">
        <div className="text-center max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <span className="inline-block px-4 py-2 bg-accent/20 text-primary rounded-full text-sm font-semibold mb-6">
              Earn Passive Income
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              Earn Without Lifting a Finger
            </h2>
          </motion.div>

          <p className="text-xl text-muted-foreground mb-12">
            If you have foot traffic, we have the solution. Provide space — we handle everything else.
          </p>

          <div className="grid sm:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg"
            >
              <div className="text-5xl font-bold text-accent mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>$0</div>
              <p className="text-foreground font-semibold">Investment Required</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg"
            >
              <div className="text-5xl font-bold text-accent mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>100%</div>
              <p className="text-foreground font-semibold">Managed Service</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg"
            >
              <div className="text-5xl font-bold text-accent mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>$500+</div>
              <p className="text-foreground font-semibold">Monthly Potential</p>
            </motion.div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#contact">
              <Button 
                className="rounded-full bg-primary text-white hover:bg-primary/90 px-10 py-6 text-lg btn-hover shadow-lg"
                data-testid="commission-cta-btn"
              >
                Start Earning Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                variant="outline"
                className="rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-white px-10 py-6 text-lg"
                data-testid="commission-schedule-btn"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book a Call
              </Button>
            </a>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// ==================== TESTIMONIALS ====================

const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Sarah Mitchell",
      role: "Office Manager, Tech Solutions Inc.",
      image: "https://images.unsplash.com/photo-1758518727888-ffa196002e59?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHBvcnRyYWl0JTIwYnVzaW5lc3N8ZW58MHx8fHwxNzcyNDgxNjI3fDA&ixlib=rb-4.1.0&q=85",
      quote: "The coffee quality is excellent and our employees love it. Plus, the monthly commission is a nice bonus for our office budget.",
      rating: 5
    },
    {
      name: "Michael Thompson",
      role: "Property Manager, Urban Living",
      image: "https://images.unsplash.com/photo-1750741268857-7e44510f867d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBtYW4lMjBwb3J0cmFpdCUyMGJ1c2luZXNzfGVufDB8fHx8MTc3MjQ4MTYyN3ww&ixlib=rb-4.1.0&q=85",
      quote: "Y&P's Daily Roast made the entire process seamless. From installation to maintenance, they handle everything professionally.",
      rating: 5
    },
    {
      name: "Dr. Jennifer Lee",
      role: "Administrator, Family Health Clinic",
      image: "https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=400",
      quote: "Our patients and staff appreciate having quality coffee available. The machine is always clean and well-stocked.",
      rating: 5
    }
  ];

  return (
    <AnimatedSection className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-testid="testimonials-section">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-secondary/50 text-primary rounded-full text-sm font-semibold mb-4">
            Testimonials
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
            What Our Partners Say
          </h2>
        </div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div key={index} variants={fadeInUp}>
              <Card className="h-full testimonial-card border-0">
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-accent text-accent" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-6 italic leading-relaxed">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center gap-4">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                    <div>
                      <h4 className="font-semibold text-foreground">{testimonial.name}</h4>
                      <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// ==================== FAQ SECTION ====================

const FAQSection = () => {
  const faqs = [
    {
      question: "How much space do I need for a coffee machine?",
      answer: "You only need approximately 1 square meter of floor space with access to a standard electrical outlet. We can work with most spaces and will assess your location during our free consultation."
    },
    {
      question: "What kind of coffee machines do you install?",
      answer: "We install premium, commercial-grade espresso and coffee machines that offer a variety of hot beverages including espresso, cappuccino, latte, hot chocolate, and more. All machines are bean-to-cup for maximum freshness."
    },
    {
      question: "How much commission can I earn?",
      answer: "Commission varies based on foot traffic and sales volume, but partners typically earn between $200-$800+ per month. High-traffic locations can earn even more."
    },
    {
      question: "Who handles maintenance and restocking?",
      answer: "We handle everything! Our team manages regular restocking, cleaning, maintenance, and any repairs needed. You won't have to lift a finger."
    },
    {
      question: "Is there any cost to get started?",
      answer: "Absolutely not. There's zero investment required from your end. We provide the machine, products, and all services at no cost to you."
    },
    {
      question: "How quickly can you install a machine?",
      answer: "Once we've assessed your location and finalized the agreement, installation typically happens within 3-5 business days."
    }
  ];

  return (
    <AnimatedSection className="py-24 md:py-32 bg-muted/30">
      <div className="max-w-4xl mx-auto px-6 md:px-12" data-testid="faq-section">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-white text-primary rounded-full text-sm font-semibold mb-4">
            FAQ
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
            Frequently Asked Questions
          </h2>
        </div>

        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="bg-white rounded-xl px-6 border-0 shadow-sm"
            >
              <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline py-6">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pb-6">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </AnimatedSection>
  );
};

// ==================== CONTACT SECTION ====================

const ContactSection = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    city: '',
    location_type: 'office',
    foot_traffic: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      trackConversion();
      toast.success('Thank you! We\'ll contact you shortly.');
      navigate('/thank-you');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatedSection className="py-24 md:py-32 bg-white" id="contact">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-testid="contact-section">
        {/* Location Availability Banner */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-accent/10 border border-accent/30 rounded-2xl p-4 mb-12 text-center"
        >
          <p className="text-foreground font-semibold">
            <span className="text-accent">Now Accepting:</span> New partner locations across the Greater Toronto Area
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <span className="inline-block px-4 py-2 bg-secondary/50 text-primary rounded-full text-sm font-semibold mb-4">
              Get In Touch
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              Let's Discuss Your Location
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Ready to turn your unused space into a passive income stream? Contact us today for a free consultation.
            </p>

            <div className="space-y-6 mb-8">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-primary rounded-xl flex items-center justify-center">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Call Us</p>
                  <a href="tel:+1 (226) 988-7379" className="text-xl font-semibold text-foreground hover:text-accent transition-colors">
                    +1 (226) 988-7379
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-primary rounded-xl flex items-center justify-center">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email Us</p>
                  <a href="mailto:info@ypdailyroast.ca" className="text-xl font-semibold text-foreground hover:text-accent transition-colors">
                    info@ypdailyroast.ca
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-primary rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Location</p>
                  <p className="text-xl font-semibold text-foreground">Ontario, Canada</p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mb-8">
              <a href="tel:+1 (226) 988-7379">
                <Button 
                  className="rounded-full bg-primary text-white hover:bg-primary/90 px-6 btn-hover"
                  data-testid="contact-call-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now
                </Button>
              </a>
              <a href="https://wa.me/12269887379" target="_blank" rel="noopener noreferrer">
                <Button 
                  className="rounded-full whatsapp-btn text-white px-6"
                  data-testid="contact-whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </a>
            </div>

            {/* Calendly CTA */}
            <div className="bg-muted/30 rounded-2xl p-6">
              <h4 className="font-semibold text-foreground mb-2">Prefer to schedule a call?</h4>
              <p className="text-sm text-muted-foreground mb-4">Book a 10-minute call at your convenience.</p>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="rounded-full" data-testid="contact-calendly-btn">
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule a Call
                </Button>
              </a>
            </div>
          </div>

          <div className="bg-muted/30 rounded-3xl p-8 md:p-10">
            <h3 className="text-2xl font-bold text-foreground mb-6">Send Us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  type="text"
                  placeholder="Your Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="h-12 rounded-xl bg-white border-border"
                  data-testid="contact-name-input"
                />
                <Input
                  type="text"
                  placeholder="Company Name"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="h-12 rounded-xl bg-white border-border"
                  data-testid="contact-company-input"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  type="email"
                  placeholder="Your Email *"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-12 rounded-xl bg-white border-border"
                  data-testid="contact-email-input"
                />
                <Input
                  type="tel"
                  placeholder="Phone Number *"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="h-12 rounded-xl bg-white border-border"
                  data-testid="contact-phone-input"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  type="text"
                  placeholder="City"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="h-12 rounded-xl bg-white border-border"
                  data-testid="contact-city-input"
                />
                <Select
                  value={formData.location_type}
                  onValueChange={(value) => setFormData({ ...formData, location_type: value })}
                >
                  <SelectTrigger className="h-12 rounded-xl bg-white border-border" data-testid="contact-location-select">
                    <SelectValue placeholder="Location Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="office">Corporate Office</SelectItem>
                    <SelectItem value="hospital">Hospital / Clinic</SelectItem>
                    <SelectItem value="school">School / University</SelectItem>
                    <SelectItem value="warehouse">Warehouse</SelectItem>
                    <SelectItem value="commercial">Commercial Building</SelectItem>
                    <SelectItem value="apartment">Apartment Complex</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Select
                value={formData.foot_traffic}
                onValueChange={(value) => setFormData({ ...formData, foot_traffic: value })}
              >
                <SelectTrigger className="h-12 rounded-xl bg-white border-border" data-testid="contact-traffic-select">
                  <SelectValue placeholder="Estimated Daily Foot Traffic" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="under-50">Under 50 people</SelectItem>
                  <SelectItem value="50-100">50-100 people</SelectItem>
                  <SelectItem value="100-200">100-200 people</SelectItem>
                  <SelectItem value="200-500">200-500 people</SelectItem>
                  <SelectItem value="500+">500+ people</SelectItem>
                </SelectContent>
              </Select>
              <Textarea
                placeholder="Tell us about your space (optional)"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="min-h-[100px] rounded-xl bg-white border-border resize-none"
                data-testid="contact-message-input"
              />
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 rounded-full bg-primary text-white hover:bg-primary/90 text-lg btn-hover"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? 'Sending...' : 'Submit Inquiry'}
                <Send className="w-5 h-5 ml-2" />
              </Button>

              {/* Response Assurance */}
              <div className="flex items-center justify-center gap-2 pt-2">
                <CheckCircle className="w-4 h-4 text-accent" />
                <p className="text-sm text-muted-foreground">
                  We review every location and respond within 24 hours.
                </p>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-16 rounded-3xl overflow-hidden shadow-lg">
          <iframe
            title="Y&P's Daily Roast Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2879.8984876453354!2d-79.76440492346406!3d43.72844724461455!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882b3e39bb83ef4b%3A0x7db2de96f9d8b2a3!2sBrampton%2C%20ON%2C%20Canada!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </AnimatedSection>
  );
};

// ==================== CHAT WIDGET ====================

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, text: "Hi! How can I help you today?", isUser: false }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [showForm, setShowForm] = useState(true);
  const visitorId = useRef(`visitor_${Date.now()}`).current;

  // Load chat history on component mount
  useEffect(() => {
    const loadChatHistory = async () => {
      try {
        const response = await axios.get(`${API}/chat/${visitorId}`);
        if (response.data && response.data.length > 0) {
          // Add stored messages to the chat
          const storedMessages = response.data.map(msg => ({
            id: new Date(msg.created_at).getTime(),
            text: msg.message,
            isUser: msg.is_user
          }));
          setMessages(storedMessages);
        }
      } catch (error) {
        console.log('No chat history found (first visit)');
      }
    };
    loadChatHistory();
  }, [visitorId]);

  // Rule-based bot response system trained on FAQs
  const getBotResponse = (userMessage) => {
    const message = userMessage.toLowerCase().trim();
    
    // Space/Requirements questions
    if (message.match(/\b(space|size|square|meter|room|floor|requirements|fit)\b/i)) {
      return "Great question! You only need approximately 1 square meter of floor space with access to a standard electrical outlet. We can work with most spaces and will assess your location during our free consultation.";
    }
    
    // Commission/Cost questions
    if (message.match(/\b(commission|earn|revenue|income|profit|money|how much|earning|monthly|per month)\b/i)) {
      return "Commission varies based on foot traffic and sales volume. Partners typically earn between $200-$800+ per month. High-traffic locations earn significantly more! For example, a location with 100+ daily visitors can generate $1000+ monthly. Our commission-based model means zero upfront costs - you only earn when the machine sells!";
    }
    
    // Machine/Product/Quality questions
    if (message.match(/\b(machine|coffee|espresso|beverage|drink|quality|bean|cappuccino|latte|chocolate)\b/i)) {
      return "We install premium, commercial-grade espresso and coffee machines that offer a variety of hot beverages including espresso, cappuccino, latte, hot chocolate, and more. All machines are bean-to-cup for maximum freshness. Each machine is regularly maintained by our team to ensure consistent, delicious coffee every time.";
    }
    
    // Maintenance/Support questions
    if (message.match(/\b(maintenance|restock|repair|clean|support|handle|service|upkeep|care)\b/i)) {
      return "We handle everything! Our team manages regular restocking, cleaning, maintenance, and any repairs needed. You won't have to lift a finger. This is one of the biggest advantages of partnering with us - it's completely hands-off for you.";
    }
    
    // Cost/Investment/Free questions
    if (message.match(/\b(cost|investment|price|upfront|fee|free|no cost|zero)\b/i)) {
      return "Absolutely zero investment required! We provide the machine, all products, and all services at no cost to you. You simply earn commission from every sale. This is a completely risk-free partnership for you.";
    }
    
    // Installation/Timeline/Speed questions
    if (message.match(/\b(install|installation|quick|fast|timeline|business days|how quickly|when|soon|wait)\b/i)) {
      return "Once we've assessed your location and finalized the agreement, installation typically happens within 3-5 business days. We work quickly to get your machine up and running! Most locations see ROI within 2-3 months.";
    }
    
    // How it works/Process questions
    if (message.match(/\b(how|works|process|setup|steps|what|does it|partnership|partnership)\b/i)) {
      return "Here's how it works: 1) We assess your location 2) Install a premium coffee machine 3) We handle all restocking & maintenance 4) You earn commission from every sale. Installation is typically 3-5 business days, and most locations see ROI within 2-3 months. Zero investment, zero hassle!";
    }
    
    // Location/Service Area questions
    if (message.match(/\b(location|locations|where|service|area|available|office|hospital|gym|warehouse|school|business|type)\b/i)) {
      return "We're currently serving Ontario with rapid expansion planned. We work with offices, hospitals, gyms, warehouses, educational institutions, and other commercial spaces. What type of location are you interested in? We'd love to explore if your space is a good fit!";
    }
    
    // Contact/Phone questions
    if (message.match(/\b(contact|call|phone|speak|talk|support|help|reach|chat|whatsapp)\b/i)) {
      return "You can reach us at +1 (226) 988-7379 or click the WhatsApp button for instant chat. Our team typically responds within 1 hour during business hours (9 AM - 5 PM EST). We're here to answer any questions!";
    }
    
    // Greeting/Introduction
    if (message.match(/\b(hi|hello|hey|thanks|thank you|yes|ok|okay|sure)\b/i)) {
      return "Hi there! 👋 Thanks for your interest in Y&P's Daily Roast! Whether you have questions about our coffee machines, commission, installation, or want to schedule a call - I'm here to help. What would you like to know?";
    }
    
    // Default responses for general conversation
    const defaultResponses = [
      "That's a great question! To better help you, could you tell me more about your location? (Type of business, foot traffic, or specific concerns)",
      "I'd love to help! What aspect interests you most - the earnings potential, the installation process, or our machine quality?",
      "Thanks for asking! Our team can provide more detailed information. Would you like to schedule a quick call at +1 (226) 988-7379 or use WhatsApp?",
      "Sounds interesting! Feel free to ask any questions about our partnership - commission, machines, installation, or anything else. What would help you the most?"
    ];
    
    return defaultResponses[Math.floor(Math.random() * defaultResponses.length)];
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = { id: Date.now(), text: inputValue, isUser: true };
    setMessages(prev => [...prev, userMessage]);
    
    // Save user message to backend
    try {
      await axios.post(`${API}/chat`, {
        visitor_id: visitorId,
        name: userInfo.name,
        email: userInfo.email,
        message: inputValue,
        isUser: true
      });
    } catch (error) {
      console.error('Failed to save user message:', error);
    }
    
    setInputValue('');

    // Get and save bot response
    setTimeout(async () => {
      const botResponse = getBotResponse(inputValue);
      const botMessage = { id: Date.now() + 1, text: botResponse, isUser: false };
      setMessages(prev => [...prev, botMessage]);
      
      // Save bot message to backend
      try {
        await axios.post(`${API}/chat`, {
          visitor_id: visitorId,
          name: 'Y&P Bot',
          email: 'bot@ypdailyroast.com',
          message: botResponse,
          isUser: false
        });
      } catch (error) {
        console.error('Failed to save bot message:', error);
      }
    }, 800);
  };

  const handleStartChat = (e) => {
    e.preventDefault();
    if (userInfo.name && userInfo.email) {
      setShowForm(false);
    }
  };

  return (
    <div className="chat-widget">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="absolute bottom-20 right-0 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl overflow-hidden z-50"
            data-testid="chat-window"
          >
            <div className="bg-primary p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center overflow-hidden">
                  <img src={logoLight} alt="Y&P's Daily Roast" className="w-8 h-8 object-contain" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">Y&P's Daily Roast</h4>
                  <p className="text-white/70 text-sm">Online now</p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-white/70 hover:text-white"
                data-testid="chat-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="h-80 flex flex-col">
              {showForm ? (
                <form onSubmit={handleStartChat} className="p-4 space-y-4">
                  <p className="text-sm text-muted-foreground">Please enter your details to start chatting:</p>
                  <Input
                    placeholder="Your Name"
                    value={userInfo.name}
                    onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })}
                    required
                    data-testid="chat-name-input"
                  />
                  <Input
                    type="email"
                    placeholder="Your Email"
                    value={userInfo.email}
                    onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
                    required
                    data-testid="chat-email-input"
                  />
                  <Button type="submit" className="w-full" data-testid="chat-start-btn">
                    Start Chat
                  </Button>
                </form>
              ) : (
                <>
                  <div className="flex-1 p-4 overflow-y-auto space-y-3">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.isUser ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                            msg.isUser 
                              ? 'bg-primary text-white rounded-br-sm' 
                              : 'bg-muted text-foreground rounded-bl-sm'
                          }`}
                        >
                          {msg.text}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="p-4 border-t border-border">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Type a message..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                        data-testid="chat-message-input"
                      />
                      <Button 
                        onClick={handleSendMessage}
                        className="px-4"
                        data-testid="chat-send-btn"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setIsOpen(!isOpen)}
        className="chat-bubble"
        data-testid="chat-toggle-btn"
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/12269887379?text=Hi%20Y%26P's%20Daily%20Roast%2C%20I%20have%20a%20question%20about%20your%20coffee%20machines."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed z-10 bottom-24 right-6 w-16 h-16 bg-white hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 z-40 overflow-hidden"
        data-testid="whatsapp-floating-btn"
        aria-label="Chat with us on WhatsApp"
        title="Chat on WhatsApp"
      >
        <img 
          src={whatsappIcon} 
          alt="WhatsApp" 
          className="w-25 h-25 object-contain"
        />
      </a>
    </div>
  );
};

// ==================== FOOTER ====================

const Footer = () => {
  return (
    <footer className="bg-foreground text-white py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img src={logoLight} alt="Y&P's Daily Roast" className="h-16 w-auto object-contain" />
            </div>
            <p className="text-white/70 mb-6 max-w-md">
              Premium coffee vending solutions for Ontario businesses. Turn your unused space into passive income.
            </p>
            <div className="flex gap-4">
              <a href="tel:+1 (226) 988-7379">
                <Button variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Us
                </Button>
              </a>
              <a href="https://wa.me/12269887379" target="_blank" rel="noopener noreferrer">
                <Button className="rounded-full whatsapp-btn">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-3">
              <li><a href="/#how-it-works" className="text-white/70 hover:text-white transition-colors">How It Works</a></li>
              <li><a href="/#locations" className="text-white/70 hover:text-white transition-colors">Locations</a></li>
              <li><a href="/#benefits" className="text-white/70 hover:text-white transition-colors">Benefits</a></li>
              <li><a href="/#about" className="text-white/70 hover:text-white transition-colors">About</a></li>
              <li><a href="/#contact" className="text-white/70 hover:text-white transition-colors">Contact</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">Contact</h4>
            <ul className="space-y-3 text-white/70">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <a href="tel:+1 (226) 988-7379" className="hover:text-white transition-colors">+1 (226) 988-7379</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <a href="mailto:info@ypdailyroast.ca" className="hover:text-white transition-colors">info@ypdailyroast.ca</a>
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>Ontario, Canada</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} Y&P's Daily Roast. All rights reserved.
          </p>
          <p className="text-white/50 text-sm">
            Proudly serving Ontario businesses
          </p>
        </div>
      </div>
    </footer>
  );
};

// ==================== BLOG PAGE ====================

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data);
      } catch (error) {
        console.error('Failed to fetch blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  return (
    <div className="min-h-screen bg-muted/30">
      <Navigation />
      
      {/* Hero */}
      <div className="pt-32 pb-16 bg-gradient-to-br from-primary to-primary/80">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-4 py-2 bg-white/20 rounded-full text-sm font-semibold mb-4">
              <BookOpen className="w-4 h-4 inline mr-2" />
              Our Blog
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Coffee & Workplace Insights
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Tips, insights, and guides about coffee solutions for offices, hospitals, schools, and commercial spaces.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Blog Grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        {loading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading articles...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-muted-foreground">No articles yet. Check back soon!</p>
          </div>
        ) : (
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {posts.map((post, index) => (
              <motion.div key={post.id} variants={fadeInUp}>
                <Link to={`/blog/${post.slug}`}>
                  <Card className="h-full overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all group cursor-pointer">
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={post.featured_image} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                        <span>{new Date(post.publish_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {post.reading_time} min read
                        </span>
                      </div>
                      <h2 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                      <p className="text-muted-foreground line-clamp-3 mb-4">
                        {post.excerpt}
                      </p>
                      <span className="inline-flex items-center text-primary font-semibold group-hover:gap-2 transition-all">
                        Read More 
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Ready to Earn Passive Income?
          </h2>
          <p className="text-muted-foreground mb-8">
            Turn your unused space into a revenue stream with our coffee vending partnership.
          </p>
          <Link to="/#contact">
            <Button className="rounded-full bg-primary text-white px-8 py-6 text-lg">
              Check My Location
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// ==================== BLOG POST PAGE ====================

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
        // Update page title
        document.title = `${response.data.title} | Y&P's Daily Roast`;
      } catch (error) {
        console.error('Failed to fetch blog post:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const shareUrl = window.location.href;
  const shareText = post?.title || '';

  const handleShare = (platform) => {
    const urls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    };
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30">
        <Navigation />
        <div className="pt-32 text-center">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-muted/30">
        <Navigation />
        <div className="pt-32 text-center">
          <h1 className="text-3xl font-bold text-foreground mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Simple markdown-like content rendering
  const renderContent = (content) => {
    return content.split('\n').map((line, index) => {
      if (line.startsWith('## ')) {
        return <h2 key={index} className="text-2xl font-bold text-foreground mt-8 mb-4">{line.replace('## ', '')}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={index} className="text-xl font-bold text-foreground mt-6 mb-3">{line.replace('### ', '')}</h3>;
      }
      if (line.startsWith('#### ')) {
        return <h4 key={index} className="text-lg font-bold text-foreground mt-4 mb-2">{line.replace('#### ', '')}</h4>;
      }
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={index} className="font-bold text-foreground my-2">{line.replace(/\*\*/g, '')}</p>;
      }
      if (line.startsWith('- ')) {
        return <li key={index} className="ml-6 text-muted-foreground list-disc">{line.replace('- ', '')}</li>;
      }
      if (line.startsWith('❌') || line.startsWith('✅')) {
        return <p key={index} className="my-1">{line}</p>;
      }
      if (line.startsWith('|')) {
        return null; // Skip table rows for now
      }
      if (line.trim() === '') {
        return <br key={index} />;
      }
      // Handle inline bold and links
      let processed = line
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary hover:underline">$1</a>');
      return <p key={index} className="text-muted-foreground my-3 leading-relaxed" dangerouslySetInnerHTML={{ __html: processed }} />;
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Article Header */}
      <div className="pt-24 pb-8 bg-muted/30">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/blog" className="inline-flex items-center text-primary hover:text-primary/80 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{post.author.charAt(0)}</span>
                </div>
                <span className="font-medium">{post.author}</span>
              </div>
              <span>•</span>
              <span>{new Date(post.publish_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.reading_time} min read
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Featured Image */}
      <div className="max-w-5xl mx-auto px-6 -mt-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-2xl overflow-hidden shadow-xl"
        >
          <img 
            src={post.featured_image} 
            alt={post.title}
            className="w-full aspect-video object-cover"
          />
        </motion.div>
      </div>

      {/* Article Content */}
      <article className="max-w-3xl mx-auto px-6 py-12">
        <div className="prose prose-lg max-w-none">
          {renderContent(post.content)}
        </div>

        {/* Share Buttons */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex items-center gap-4">
            <span className="font-semibold text-foreground">Share this article:</span>
            <div className="flex gap-2">
              <button 
                onClick={() => handleShare('twitter')}
                className="w-10 h-10 rounded-full bg-[#1DA1F2] text-white flex items-center justify-center hover:opacity-80 transition-opacity"
              >
                <Twitter className="w-5 h-5" />
              </button>
              <button 
                onClick={() => handleShare('linkedin')}
                className="w-10 h-10 rounded-full bg-[#0A66C2] text-white flex items-center justify-center hover:opacity-80 transition-opacity"
              >
                <Linkedin className="w-5 h-5" />
              </button>
              <button 
                onClick={() => handleShare('facebook')}
                className="w-10 h-10 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-80 transition-opacity"
              >
                <Facebook className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </article>

      {/* CTA Section */}
      <div className="bg-primary py-16">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Want to Earn Passive Income From Your Location?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Turn your unused space into a revenue stream. Zero investment, fully managed, and earn commission on every cup sold.
          </p>
          <Button 
            onClick={() => setIsModalOpen(true)}
            className="rounded-full bg-white text-primary hover:bg-white/90 px-8 py-6 text-lg font-semibold"
            data-testid="blog-cta-btn"
          >
            Check My Location
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>

      <Footer />
      
      {/* Qualification Modal */}
      <QualificationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

// ==================== THANK YOU PAGE ====================

const ThankYouPage = () => {
  return (
    <div className="min-h-screen bg-muted/30 pt-20">
      <Navigation />
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8">
            <Check className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-4">Thank You!</h1>
          <p className="text-lg text-muted-foreground mb-8">
            We've received your inquiry and will contact you within 24 hours to discuss your location.
          </p>
          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <h3 className="font-semibold text-foreground mb-4">What happens next?</h3>
            <ul className="text-left space-y-3 text-muted-foreground">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <span>Our team will review your location details</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <span>We'll call you to discuss the partnership</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <span>Schedule an on-site assessment</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <span>Install your machine within 3-5 business days</span>
              </li>
            </ul>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button variant="outline" className="rounded-full">
                Back to Home
              </Button>
            </Link>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="rounded-full bg-primary text-white">
                <Calendar className="w-4 h-4 mr-2" />
                Schedule a Call Now
              </Button>
            </a>
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

// ==================== ADMIN DASHBOARD ====================

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/leads`, {
        auth: { username: 'admin', password }
      });
      setLeads(response.data);
      setIsAuthenticated(true);
      fetchStats();
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const url = statusFilter === 'all' 
        ? `${API}/admin/leads` 
        : `${API}/admin/leads?status=${statusFilter}`;
      const response = await axios.get(url, {
        auth: { username: 'admin', password }
      });
      setLeads(response.data);
    } catch (error) {
      toast.error('Failed to fetch leads');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        auth: { username: 'admin', password }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/leads/${leadId}/status`, 
        { status: newStatus },
        { auth: { username: 'admin', password } }
      );
      toast.success('Status updated');
      fetchLeads();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const exportCSV = () => {
    window.open(`${API}/admin/leads/export/csv`, '_blank');
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchLeads();
    }
  }, [statusFilter, isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center">Admin Login</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password-input"
              />
              <Button type="submit" className="w-full" disabled={loading} data-testid="admin-login-btn">
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Lead Dashboard</h1>
            <p className="text-muted-foreground">Manage your coffee machine leads</p>
          </div>
          <div className="flex gap-4">
            <Button variant="outline" onClick={exportCSV} data-testid="admin-export-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button variant="outline" onClick={() => setIsAuthenticated(false)} data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Leads</p>
                  <p className="text-3xl font-bold">{stats.total}</p>
                </div>
                <BarChart3 className="w-8 h-8 text-primary" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">New</p>
                  <p className="text-3xl font-bold text-blue-700">{stats.new}</p>
                </div>
                <Badge variant="secondary" className="bg-blue-100 text-blue-700">New</Badge>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-yellow-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600">Contacted</p>
                  <p className="text-3xl font-bold text-yellow-700">{stats.contacted}</p>
                </div>
                <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">Contacted</Badge>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Closed</p>
                  <p className="text-3xl font-bold text-green-700">{stats.closed}</p>
                </div>
                <Badge variant="secondary" className="bg-green-100 text-green-700">Closed</Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <Filter className="w-5 h-5 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48" data-testid="admin-status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Leads</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Leads Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-semibold">Name</th>
                    <th className="text-left p-4 font-semibold">Contact</th>
                    <th className="text-left p-4 font-semibold">Location</th>
                    <th className="text-left p-4 font-semibold">Status</th>
                    <th className="text-left p-4 font-semibold">Date</th>
                    <th className="text-left p-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id} className="border-b border-border hover:bg-muted/20">
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{lead.name}</p>
                          <p className="text-sm text-muted-foreground">{lead.company}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-sm">{lead.email}</p>
                          <p className="text-sm text-muted-foreground">{lead.phone}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-sm">{lead.location_type?.replace('_', ' ').toUpperCase()}</p>
                          <p className="text-sm text-muted-foreground">{lead.city}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <Select 
                          value={lead.status || 'new'} 
                          onValueChange={(value) => updateLeadStatus(lead.id, value)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">
                              <Badge variant="secondary" className="bg-blue-100 text-blue-700">New</Badge>
                            </SelectItem>
                            <SelectItem value="contacted">
                              <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">Contacted</Badge>
                            </SelectItem>
                            <SelectItem value="closed">
                              <Badge variant="secondary" className="bg-green-100 text-green-700">Closed</Badge>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setSelectedLead(lead)}
                          data-testid={`view-lead-${lead.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Lead Detail Modal */}
        <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Lead Details</DialogTitle>
            </DialogHeader>
            {selectedLead && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{selectedLead.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Company</p>
                    <p className="font-medium">{selectedLead.company || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{selectedLead.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="font-medium">{selectedLead.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">City</p>
                    <p className="font-medium">{selectedLead.city || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Location Type</p>
                    <p className="font-medium">{selectedLead.location_type?.replace('_', ' ').toUpperCase()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Foot Traffic</p>
                    <p className="font-medium">{selectedLead.foot_traffic || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge 
                      variant="secondary" 
                      className={
                        selectedLead.status === 'new' ? 'bg-blue-100 text-blue-700' :
                        selectedLead.status === 'contacted' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }
                    >
                      {selectedLead.status || 'New'}
                    </Badge>
                  </div>
                </div>
                {selectedLead.message && (
                  <div>
                    <p className="text-sm text-muted-foreground">Message</p>
                    <p className="font-medium bg-muted/30 p-3 rounded-lg mt-1">{selectedLead.message}</p>
                  </div>
                )}
                <div className="flex gap-2 pt-4">
                  <a href={`tel:${selectedLead.phone}`} className="flex-1">
                    <Button className="w-full">
                      <Phone className="w-4 h-4 mr-2" />
                      Call
                    </Button>
                  </a>
                  <a href={`mailto:${selectedLead.email}`} className="flex-1">
                    <Button variant="outline" className="w-full">
                      <Mail className="w-4 h-4 mr-2" />
                      Email
                    </Button>
                  </a>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// ==================== LANDING PAGES ====================

const LandingPageTemplate = ({ 
  title, 
  subtitle, 
  description, 
  benefits, 
  caseStudy,
  image 
}) => {
  return (
    <div className="min-h-screen">
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-20 hero-gradient coffee-pattern">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                {title}
              </h1>
              <p className="text-xl text-muted-foreground mb-8">{subtitle}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#contact-form">
                  <Button className="rounded-full bg-primary text-white px-8 py-6 text-lg">
                    Get Started
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
                <a href="tel:+1 (226) 988-7379">
                  <Button variant="outline" className="rounded-full px-8 py-6 text-lg">
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now
                  </Button>
                </a>
              </div>
            </div>
            <div>
              <img src={image} alt={title} className="rounded-3xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-lg text-muted-foreground leading-relaxed">{description}</p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose Us?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, i) => (
              <Card key={i} className="border-0 shadow-lg">
                <CardContent className="p-8">
                  <CheckCircle className="w-10 h-10 text-accent mb-4" />
                  <h3 className="font-bold text-xl mb-2">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study */}
      {caseStudy && (
        <section className="py-20 bg-primary text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-6">Success Story</h2>
            <blockquote className="text-xl italic mb-4">"{caseStudy.quote}"</blockquote>
            <p className="text-white/70">{caseStudy.author}</p>
          </div>
        </section>
      )}

      {/* Contact Form */}
      <section className="py-20 bg-white" id="contact-form">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-8">Get Your Free Quote</h2>
          <ContactFormSimple />
        </div>
      </section>

      <Footer />
    </div>
  );
};

const ContactFormSimple = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', city: '', location_type: 'office', foot_traffic: '', message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      trackConversion();
      navigate('/thank-you');
    } catch {
      toast.error('Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <Input placeholder="Your Name *" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
        <Input placeholder="Company" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Input type="email" placeholder="Email *" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
        <Input placeholder="Phone *" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
      </div>
      <Input placeholder="City" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} />
      <Textarea placeholder="Message (optional)" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} />
      <Button type="submit" disabled={isSubmitting} className="w-full rounded-full py-6 text-lg">
        {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
      </Button>
    </form>
  );
};

// Office Coffee Page
const OfficeCoffeePage = () => (
  <LandingPageTemplate
    title="Office Coffee Machines That Pay for Themselves"
    subtitle="Keep your team energized while earning commission on every cup."
    description="Transform your office break room into a premium coffee experience. Our commercial-grade machines serve barista-quality beverages that boost productivity and morale. Best of all, you earn passive income with zero investment or maintenance responsibilities."
    benefits={[
      { title: "Boost Employee Morale", description: "Quality coffee keeps your team happy and productive throughout the day." },
      { title: "Zero Investment", description: "We provide the machine, products, and maintenance at no cost to you." },
      { title: "Monthly Commission", description: "Earn $200-$500+ per month depending on your office size." }
    ]}
    caseStudy={{ quote: "The coffee quality is amazing and our team loves it. The monthly commission has become a nice addition to our office budget.", author: "Sarah M., Tech Solutions Inc." }}
    image="https://images.unsplash.com/photo-1497366216548-37526070297c?w=800"
  />
);

// Hospital Coffee Page
const HospitalCoffeePage = () => (
  <LandingPageTemplate
    title="Coffee Vending for Healthcare Facilities"
    subtitle="Comfort for patients, visitors, and staff around the clock."
    description="Healthcare workers and visitors need quality coffee during long shifts and difficult times. Our hygienic, hospital-grade vending machines provide 24/7 access to premium beverages while generating passive income for your facility."
    benefits={[
      { title: "24/7 Availability", description: "Coffee when staff and visitors need it most, day or night." },
      { title: "Hospital-Grade Hygiene", description: "Touchless options and regular sanitization protocols." },
      { title: "Supporting Your Mission", description: "Commission funds can support patient care programs." }
    ]}
    caseStudy={{ quote: "Our staff and patients really appreciate having quality coffee available 24/7. The machine is always clean and well-maintained.", author: "Dr. Jennifer L., Family Health Clinic" }}
    image="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800"
  />
);

// School Coffee Page
const SchoolCoffeePage = () => (
  <LandingPageTemplate
    title="Coffee Solutions for Schools & Universities"
    subtitle="Keep faculty energized and generate funds for your institution."
    description="Educational facilities have high foot traffic and need quality coffee solutions. Our machines serve students, faculty, and visitors while generating commission that can support school programs and initiatives."
    benefits={[
      { title: "High Foot Traffic ROI", description: "Universities see excellent returns due to constant student and staff flow." },
      { title: "Fund School Programs", description: "Use commission to support extracurricular activities." },
      { title: "Convenient Locations", description: "Place machines in libraries, cafeterias, or staff rooms." }
    ]}
    image="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800"
  />
);

// Property Management Page
const PropertyManagementPage = () => (
  <LandingPageTemplate
    title="Property Management Coffee Partnerships"
    subtitle="Add value to your properties while earning commission."
    description="Property managers can enhance tenant satisfaction and generate additional revenue by hosting our coffee machines in lobbies, common areas, and amenity spaces. No maintenance headaches - we handle everything."
    benefits={[
      { title: "Tenant Satisfaction", description: "Add a premium amenity that tenants and visitors love." },
      { title: "Multiple Properties", description: "Partner with us across your entire portfolio." },
      { title: "Hassle-Free Income", description: "We manage everything - you just collect commission." }
    ]}
    caseStudy={{ quote: "The coffee machine in our lobby has been a hit with residents. It's an amenity that adds value with zero effort on our part.", author: "Michael T., Urban Living Property Management" }}
    image="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800"
  />
);

// ==================== EXIT INTENT POPUP ====================

const ExitIntentPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [formData, setFormData] = useState({ phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    // Check if popup was already shown in this session
    const alreadyShown = sessionStorage.getItem('exitPopupShown');
    if (alreadyShown) {
      setHasShown(true);
      return;
    }

    // Exit intent detection for desktop
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !hasShown) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
        trackEvent('exit_intent_popup_shown');
      }
    };

    // Add delay before enabling exit intent (don't show immediately)
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 5000); // 5 second delay

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [hasShown]);

  const handleClose = () => {
    setIsVisible(false);
    trackEvent('exit_intent_popup_closed');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.phone) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: '',
        email: '',
        phone: formData.phone,
        company: '',
        city: '',
        location_type: 'other',
        foot_traffic: '',
        message: 'Exit intent popup submission - Quick callback request'
      });

      trackEvent('exit_intent_popup_submit');
      trackConversion();
      setIsSuccess(true);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        onClick={handleClose}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

        {/* Popup Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 50 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="exit-intent-popup"
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted transition-colors z-10"
            data-testid="exit-popup-close"
          >
            <X className="w-5 h-5 text-foreground" />
          </button>

          {isSuccess ? (
            /* Success State */
            <div className="p-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", damping: 15 }}
                className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <Check className="w-10 h-10 text-white" />
              </motion.div>
              <h2 className="text-2xl font-bold text-foreground mb-3">We'll Call You Soon!</h2>
              <p className="text-muted-foreground mb-6">
                Our team will reach out within 24 hours to discuss how your location can earn passive income.
              </p>
              <Button
                onClick={handleClose}
                className="rounded-full bg-primary text-white px-8"
              >
                Continue Browsing
              </Button>
            </div>
          ) : (
            /* Main Popup Content */
            <>
              {/* Header with gradient */}
              <div className="bg-gradient-to-br from-primary to-primary/80 p-8 text-white text-center">
                <motion.div
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm font-medium mb-4">
                    Wait! Before You Go...
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold mb-2">
                    You Could Be Earning
                  </h2>
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.4, type: "spring" }}
                    className="text-5xl md:text-6xl font-bold text-accent mb-1"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    $600+
                  </motion.div>
                  <p className="text-white/90">per month in passive income</p>
                </motion.div>
              </div>

              {/* Body */}
              <div className="p-8">
                {/* Earnings Breakdown */}
                <div className="bg-muted/30 rounded-2xl p-4 mb-6">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">80 cups/day × $2.50</span>
                      <span className="font-semibold text-accent">$200/day</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">$200 × 30 days</span>
                      <span className="font-semibold text-accent">$6,000/month</span>
                    </div>
                    <div className="border-t border-border pt-2 flex justify-between items-center">
                      <span className="font-medium text-foreground">Your 10% commission</span>
                      <span className="font-bold text-lg text-primary">$600/month</span>
                    </div>
                  </div>
                </div>

                {/* Quick Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-3 text-center">
                      Leave your number and we'll call you to discuss your location's earning potential.
                    </p>
                    <Input
                      type="tel"
                      placeholder="Your phone number"
                      value={formData.phone}
                      onChange={(e) => setFormData({ phone: e.target.value })}
                      required
                      className="h-14 rounded-xl bg-muted/30 border-border text-center text-lg"
                      data-testid="exit-popup-phone"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting || !formData.phone}
                    className="w-full h-14 rounded-full bg-primary text-white hover:bg-primary/90 text-lg font-semibold shadow-lg"
                    data-testid="exit-popup-submit"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                        />
                        Sending...
                      </span>
                    ) : (
                      <>
                        <Phone className="w-5 h-5 mr-2" />
                        Request a Call Back
                      </>
                    )}
                  </Button>
                </form>

                {/* Trust indicators */}
                <div className="flex items-center justify-center gap-4 mt-6 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-accent" />
                    No obligation
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-accent" />
                    24hr response
                  </span>
                </div>

                {/* Skip link */}
                <button
                  onClick={handleClose}
                  className="w-full text-center text-sm text-muted-foreground hover:text-foreground mt-4 underline"
                >
                  No thanks, I'll keep browsing
                </button>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ==================== HOME PAGE ====================

const HomePage = () => {
  useEffect(() => {
    initGTM();
    initFBPixel();
  }, []);

  return (
    <>
      <Navigation />
      <SocialProofCounter />
      <HeroSection />
      {/* <TrustedLocationsSection /> */}
      <TrustBenefitsBar />
      <RevenueCalculator />
      <QualificationSection />
      <HowItWorksSection />
      <ExampleEarningsSection />
      <LocationsSection />
      <BenefitsSection />
      <FounderSection />
      <CommissionSection />
      <TestimonialsSection />
      <FAQSection />
      <ContactSection />
      <Footer />
      <ChatWidget />
      <ExitIntentPopup />
    </>
  );
};

// ==================== MAIN APP ====================

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/office-coffee-machines" element={<OfficeCoffeePage />} />
          <Route path="/hospital-coffee-vending" element={<HospitalCoffeePage />} />
          <Route path="/school-coffee-solutions" element={<SchoolCoffeePage />} />
          <Route path="/property-management-partnerships" element={<PropertyManagementPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
